/**
 * @file AcceptCookies.tsx
 * @description
 * -----------------------------------------------------
 * Componente de cookies
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-07-28
 * @modified 2025-10-01
 * @author DLTCode
 * @author Ismael Benabdellah
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { useEffect, useState, useCallback } from "react";
import { useAcceptCookies } from "@/context/CookiesProvider";
import ImageComponent from "./ImageComponent";
import { useTranslations } from "next-intl";
import ButtonComponent from "./ButtonComponent";
import { MinusIcon, PlusIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePathname } from "next/navigation";
import { FocusTrap } from "focus-trap-react";
import { createPortal } from "react-dom";
export default function CookiesModal() {
  const {
    areCookiesAccepted,
    isLoading,
    acceptAllCookies,
    acceptRequiredOnly,
    isPending,
    showCustomizeCookies,
    setShowCustomizeCookies,
    showStrictCookiesDescription,
    setShowStrictCookiesDescription
  } = useAcceptCookies();
  const [mounted, setMounted] = useState(false);
  const [bannerElement, setBannerElement] = useState<HTMLElement | null>(null);
  const pathname = usePathname();
  const t = useTranslations("Cookies");
  const bannerRef = useCallback((node: HTMLElement | null) => {
    setBannerElement(node);
  }, []);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  useEffect(() => {
    if (!mounted || !bannerElement) return;
    const footer = document.querySelector("footer");
    if (!footer) return;
    const updatePosition = () => {
      const footerRect = footer.getBoundingClientRect();
      const isFooterVisible = footerRect.top < window.innerHeight;
      bannerElement.style.bottom = isFooterVisible ? `${window.innerHeight - footerRect.top}px` : "1rem";
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [mounted, bannerElement, pathname]);
  if (!mounted || isLoading || areCookiesAccepted) return null;
  return createPortal(<FocusTrap active>
      <AnimatePresence>
        <motion.dialog open aria-modal="true" aria-labelledby="cookies-dialog-title" aria-describedby="cookies-dialog-desc" ref={bannerRef} initial={{
        opacity: 0,
        translateY: 400
      }} animate={{
        opacity: 1,
        translateY: 0
      }} exit={{
        opacity: 0,
        translateY: 400
      }} transition={{
        duration: 0.66,
        type: "spring",
        bounce: 0.25
      }} className="cookies-banner" tabIndex={-1}>
          <section className="cookies">
            <ImageComponent src={"/assets/img/cookies.webp"} alt={t("cookiesImg")} width={448} height={120} zoomable={false} preload />
            <section className="cookies__content">
              <section className="cookies__header">
                <h2 id="cookies-dialog-title">
                  {t(showCustomizeCookies ? "customizeYourPreferences" : "useOfCookies")}
                </h2>
              </section>
              <section className="cookies__description" id="cookies-dialog-desc">
                {!showCustomizeCookies && <p>
                    {t("description")}{" "}
                    <a href="https://dltcode.es/politica-cookies" target="_blank">
                      {t("cookiesPolicy")}
                    </a>
                  </p>}
                {showCustomizeCookies && <section className="cookies__description__strict">
                    <section className="cookies__description__strict__row">
                      <button onClick={() => setShowStrictCookiesDescription(!showStrictCookiesDescription)} className="cookies__description__strict__row__button" aria-label={t("ariaStrictCookies")} title={t("ariaStrictCookies")}>
                        {showStrictCookiesDescription ? <MinusIcon role="img" aria-hidden /> : <PlusIcon role="img" aria-hidden />}
                        <p>{t("strictCookies")}</p>
                      </button>
                      <br />
                      <p className="cookies__description__strict--active">
                        {t("activeStrictCookies")}
                      </p>
                    </section>
                    {showStrictCookiesDescription && <p>{t("descriptionStrictCookies")}</p>}
                  </section>}
              </section>
              <section className="cookies__footer">
                {!showCustomizeCookies && <ButtonComponent label="buttonCustomizeCookies" variant="ghost" className="btn--full cookies__footer__button" onClick={() => setShowCustomizeCookies(true)} />}
                <ButtonComponent label="buttonDeclineCookies" color="red" className="btn--full cookies__footer__button" onClick={acceptRequiredOnly} loading={isPending} />
                <ButtonComponent label="buttonAcceptCookies" className="btn--full cookies__footer__button" onClick={acceptAllCookies} loading={isPending} />
              </section>
            </section>
          </section>
        </motion.dialog>
      </AnimatePresence>
    </FocusTrap>, document.body);
}